define((require) => {
    "use strict";

    const _                    = require("underscore");
    const Backbone             = require("backbone");
    const CompositeWidgetModel = require("shared/ui/experience/widgets/compositeWidget/CompositeWidgetModel");
    const RestApi              = require("shared/services/RestApi");
    const ImprovedModel        = require("shared/zbone/ImprovedModel");
    const OverlayScene         = require("shared/ui/experience/OverlayScene");
    const BackgroundScene      = require("shared/ui/experience/BackgroundScene");
    const Backdrop             = require("shared/ui/experience/Backdrop");
    const SceneTransitionTypes = require("shared/ui/experience/SceneTransitionTypes").default;
    const SceneCollection      = require("shared/ui/experience/SceneCollection");
    const Size                 = require("shared/util/Size");
    const Scale                = require("shared/util/Scale");

    const DEFAULT_NAME                 = "(Untitled Experience)";
    const DEFAULT_TRANSITION           = SceneTransitionTypes.SLIDE_FROM_RIGHT;
    const DEFAULT_SCENE_AUTOPLAY_DELAY = 5000;
    const DEFAULT_BEHAVIOR = [{
        name: "",
        connection: "",
        branches: "",
        variables: "",
        conditions: []
    }];

    /**
     * The experience model.
     *
     * @class ExperienceModel
     * @constructor
     */
    const ExperienceModel = ImprovedModel.extend("Experience", {

        url() {
            return RestApi.pathToSpecificResource(RestApi.Collections.EXPERIENCES, this.get("id"));
        },

        relations: [{
            type: Backbone.Many,
            key: "scenes",
            collectionType: SceneCollection
        }, {
            type: Backbone.One,
            key: "overlayScene",
            relatedModel: OverlayScene
        }, {
            type: Backbone.One,
            key: "backgroundScene",
            relatedModel: BackgroundScene,
            reverseRelation: {
                key: "experience"
            }
        }, {
            type: Backbone.One,
            key: "backdrop",
            relatedModel: Backdrop,
            reverseRelation: {
                key: "experience"
            }
        }],

        defaults: {

            /**
             * The <tt>SceneCollection</tt>, initially defined by "backbone.associations" (the empty array is needed).<p>
             *
             * The <tt>ExperienceModel</tt> is born with an empty collection of scenes - for now, the editor is responsible
             * for adding at least one scene before persisting.
             *
             * @see  EditorController#editNewExperience
             * @type {SceneCollection}
             */
            scenes: [],

            /**
             * Defaults to <tt>OverlayScene</tt>, handled by "backbone.associations" (the empty object is needed).
             *
             * @type {OverlayScene}
             */
            overlayScene: {},

            /**
             * Defaults to <tt>BackgroundScene</tt>, handled by "backbone.associations" (the empty object is needed).
             *
             * @type {BackgroundScene}
             */
            backgroundScene: {},

            name: DEFAULT_NAME,
            width: null,
            height: null,
            companyId: null,
            transition: DEFAULT_TRANSITION.id,
            autoPlayDelay: DEFAULT_SCENE_AUTOPLAY_DELAY,
            behaviorConfig: DEFAULT_BEHAVIOR,
            state: null, // also used by Viewer
            // Use a plain object in order for backbone.association to create a default backdrop model, which is
            // needed when we render data into view. We cannot just do new Backdrop() here,
            // because the defaults go to prototype and the new instance will be shared among all experiences
            backdrop: {},

            /**
             * Represents the current scale of the experience, prior to it's
             * defined size (width and height).
             *
             * When viewed, the experience might be contained within a viewport of
             * a different size, resulting in a scaled experience.
             *
             * Note that this is a transient property.
             *
             * @see  #scaledWidth
             * @see  #scaledHeight
             *
             * @type {Scale}
             */
            scale: new Scale(),

            /**
             * Image integrations are a part of the experience model as they are critical
             * for the rendering of images when third party image integrations are used (e.g. Scene7)
             * This is necessary as we cannot wait for more requests to finished before we start rendering (See MOS-3438)
             */
            integrations: [],
            language: "en-US",
            experienceMetadata: {
                dynamicProductIds: []
            },

            emailModeEnabled: false
        },

        computed: {
            scaledWidth: {
                deps: ["scale", "width"],
                value(scale, width) {
                    return scale.x * width;
                }
            },

            scaledHeight: {
                deps: ["scale", "height"],
                value(scale, height) {
                    return scale.y * height;
                }
            }
        },

        /**
         * Constructor.
         */
        constructor() {
            this.__super();

            // Update all contained scenes (and eventually their contained widgets) with the new scale.
            this.listenTo(this, "change:scale", (model, scale) => {
                _.invoke(this.getAllScenes(), "set", "scale", scale);
            });
        },

        /**
         * Returns a convenience array containing all widgets, including the grouped, overlay, and background widgets.
         *
         * @return {AbstractWidgetModel[]} Array referencing all widgets.
         */
        getAllWidgets() {
            return _.flatten(_.map(this.getAllScenes(), (scene) => {
                const widgets = scene.get("widgets");
                const subModels = widgets
                    .filter((widget) => widget instanceof CompositeWidgetModel && widget.has("group"))
                    .map((compositeWidget) => compositeWidget.get("group").models);
                return [widgets.models, subModels];
            }));
        },

        /**
         * Returns a convenience array containing all scenes, including the overlay and background scenes.
         *
         * @return {Scenes[]} Array referencing all scenes.
         */
        getAllScenes() {
            const scenes = _.clone(this.get("scenes").models);
            scenes.push(this.get("overlayScene"), this.get("backgroundScene"));
            return scenes;
        },

        /**
         * Returns the number of scenes (<tt>length</tt> of the inner scenes collection), excluding
         * overlay and background scenes.
         *
         * @return {Number} The number of scenes.
         */
        getNumberOfScenes() {
            return this.get("scenes").length;
        },

        getPixelPosition(point) {
            const w = this.get("width");
            const h = this.get("height");
            const pixelPosition = {};

            if (point.hasOwnProperty("left")) {
                pixelPosition.left = (point.left / 100) * w;
            }

            if (point.hasOwnProperty("top")) {
                pixelPosition.top = (point.top / 100) * h;
            }

            return pixelPosition;
        },

        getPixelSize(size) {
            const w = this.get("width");
            const h = this.get("height");
            const pixelSize = {};

            if (size.hasOwnProperty("width")) {
                pixelSize.width = (size.width / 100) * w;
            }

            if (size.hasOwnProperty("height")) {
                pixelSize.height = (size.height / 100) * h;
            }

            return pixelSize;
        },

        getPercentagePosition(point) {
            const w = this.get("width");
            const h = this.get("height");
            const percentagePosition = {};

            if (point.hasOwnProperty("left")) {
                percentagePosition.left = (point.left / w) * 100;
            }

            if (point.hasOwnProperty("top")) {
                percentagePosition.top = (point.top / h) * 100;
            }

            return percentagePosition;
        },

        /**
         * Returns a given size in pixels calculated as a percentage of the size of this
         * experience.
         * @param {Object} sizeInPixels An object with width and height properties.
         * @returns {Size}
         */
        getPercentageSize(sizeInPixels) {
            let percentageWidth;
            let percentageHeight;

            if (sizeInPixels.hasOwnProperty("width")) {
                percentageWidth = (sizeInPixels.width / this.get("width")) * 100;
            }

            if (sizeInPixels.hasOwnProperty("height")) {
                percentageHeight = (sizeInPixels.height / this.get("height")) * 100;
            }

            return new Size(percentageWidth, percentageHeight);
        },

        /**
         * Gets the dimensions of the experience as an object containing a width and height property. If a bounding box
         * size is specified, the dimensions are scaled to fit within this box.
         *
         * @param {Object} [boundingBoxSize] Bounding box size. You may choose to specify one of width and height or both.
         * @returns {Object} The scaled dimensions of the experience. Contains three properties: width, height, and scale.
         */
        getDimensions(boundingBoxSize) {
            const height = this.get("height");
            const width = this.get("width");

            let scaleRatio = 1.0;

            if (boundingBoxSize) {
                scaleRatio = Math.min(
                    1.0,
                    boundingBoxSize.height ? boundingBoxSize.height / height : 1.0,
                    boundingBoxSize.width ? boundingBoxSize.width / width : 1.0
                );
            }

            return {
                width: Math.round(width * scaleRatio),
                height: Math.round(height * scaleRatio),
                scale: scaleRatio
            };
        },

        /**
         * Gets the scene with the given ID, or null if no scene matching the ID is found.
         * @param {String} sceneId The scene ID.
         * @returns {Scene} The scene matching the given ID.
         */
        getSceneById(sceneId) {
            if (this.get("overlayScene").get("id") === sceneId) {
                return this.get("overlayScene");
            }

            if (this.get("backgroundScene").get("id") === sceneId) {
                return this.get("backgroundScene");
            }

            return this.get("scenes").findWhere({id: sceneId}) || null;
        },

        getSceneBySceneNumber(sceneNum) {
            if (sceneNum === "overlay") {
                return this.get("overlayScene");
            } else if (sceneNum === "background") {
                return this.get("backgroundScene");
            } else if (sceneNum >= 0) {
                return this.get("scenes").at(sceneNum - 1);
            }

            return null;
        },

        /**
         * Returns the size of the experience, in pixels.
         *
         * Pass <tt>true</tt> to get the scaled experience size.
         *
         * @param  {Boolean} scaled Whether to return the scaled experience
         * size or the original size.
         *
         * @see  scale
         *
         * @return {Size} The size of the experience, either the original
         * (default behavior), or if specified, the scaled size.
         */
        getSize(scaled) {
            if (scaled) {
                return new Size(this.get("scaledWidth"), this.get("scaledHeight"));
            }

            return new Size(this.get("width"), this.get("height"));
        },

        isInactive() {
            // Only inactive filters will actually push the state, and then as "Inactive", but for legacy reasons,
            // we have to handle cases where a state is marshaled anyway... :/
            return this.get("state") === "Inactive"; // Viewer thing, do not include complex enum state for this!
        },

        /**
         * Returns the matching integration on the experience model
         *
         * @param {String} type Integration type name to lookup in the array of integrations
         * @returns {Object} The matching integration data object, returns undefined if type wasn't found
         */
        getIntegration(type) {
            const experienceIntegrations = this.get("integrations");
            return _.find(experienceIntegrations, (integration) => integration.type === type);
        }

    }, {
        type: "Experience",
        DEFAULT_NAME
    });

    return ExperienceModel;
});



// WEBPACK FOOTER //
// ./src/shared/ui/experience/ExperienceModel.js